import React, { useState } from "react";
import {
  AlignLeft,
  BellRing,
  CircleUserRound,
  ChevronDown,
} from "lucide-react";
import dashboard from "../assets/dashboard-icon.svg";
import survey from "../assets/survey-icon.svg";
import setting from "../assets/settings-svgrepo-com.svg";
import logo from "../assets/logo.svg";
import Dashboard from "../Pages/Dashboard";
import SurveyManagement from "../Pages/SurveyManagement";
import Setting from "../Pages/Setting";
import CustomerManagement from "../Pages/CustomerManagement";
import UserManagement from "../Pages/UserManagement";
import RoleManagement from "../Pages/RoleManagement";
import Footer from "./Footer";
import { useAuthStore } from "../stores/adminStore";

const Sidebar = () => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("dashboard");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { logout } = useAuthStore();

  const clearToken = () => {
    logout(); // Use store's logout function
  };

  const toggleSidebar = () => {
    setOpen(!open);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const sideMenu = [
    { title: "dashboard", icon: dashboard },
    { title: "surveyManagement", icon: survey },
    {
      title: "setting",
      icon: setting,
      submenu: [
        { title: "customerManagement", label: "Customer Management" },
        { title: "userManagement", label: "User Management" },
        { title: "roleManagement", label: "Role Management" },
      ],
    },
  ];

  const renderPage = () => {
    switch (current) {
      case "dashboard":
        return <Dashboard />;
      case "surveyManagement":
        return <SurveyManagement />;
      case "setting":
        return <Setting />;
      case "customerManagement":
        return <CustomerManagement />;
      case "userManagement":
        return <UserManagement />;
      case "roleManagement":
        return <RoleManagement />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <div className="flex">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 w-full flex justify-between items-center p-5 bg-white shadow-md z-50">
        <div className="flex items-center gap-5">
          <img src={logo} alt="Company Logo" className="w-10 h-10" />
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-md hover:bg-gray-100"
          >
            <AlignLeft className="text-[#1e5190]" />
          </button>
          <h1 className="text-[#1e5190] font-bold text-2xl">RCAATS</h1>
        </div>

        <div className="flex items-center gap-5">
          <BellRing className="text-[#1e5190]" />
          <button
            className="flex items-center gap-2 p-2 rounded-md hover:bg-gray-100"
            onClick={toggleDropdown}
          >
            <CircleUserRound className="text-[#1e5190]" />
            <ChevronDown className="text-[#1e5190]" />
          </button>
        </div>
      </nav>

      {/* Dropdown */}
      {isDropdownOpen && (
        <div className="fixed top-16 right-5 bg-white shadow-lg rounded-md z-50 w-48">
          <div className="flex flex-col">
            {/* Dropdown Items */}
            <button className="p-3 text-left border-b border-gray-200 hover:bg-gray-100 hover:text-teal-500">
              <p className="text-gray-700">{localStorage.getItem("email")}</p>
            </button>
            <button className="p-3 text-left border-b border-gray-200 hover:bg-gray-100 hover:text-teal-500">
              <p className="text-gray-700">Settings</p>
            </button>
            <button
              className="p-3 text-left hover:bg-gray-100 hover:text-teal-500"
              onClick={clearToken}
            >
              <p className="text-gray-700">Logout</p>
            </button>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-40 h-screen pt-20 transition-transform duration-300 ${
          open ? "translate-x-0 w-72" : "-translate-x-30 w-20"
        } sm:translate-x-0 bg-[#063269]`}
      >
        <div className="h-full flex flex-col">
          {sideMenu.map((menu, index) => (
            <div key={index} className="relative ">
              <button
                className={`hover:bg-[#a9e5f7] w-full ${
                  current === menu.title ? "bg-[#a9e5f7]" : ""
                }`}
                onClick={() => {
                  if (menu.submenu) {
                    setSettingsOpen(!settingsOpen);
                  } else {
                    setCurrent(menu.title);
                  }
                }}
                onMouseEnter={() => menu.submenu && setSettingsOpen(true)}
                onMouseLeave={() => menu.submenu && setSettingsOpen(false)}
              >
                <div className="flex items-center gap-2 p-5 rounded-md ">
                  <img
                    src={menu.icon}
                    className="w-8"
                    style={{
                      filter:
                        current === menu.title
                          ? "invert(15%) sepia(86%) saturate(2000%) hue-rotate(195deg) brightness(75%) contrast(110%)"
                          : "invert(100%) sepia(10%) saturate(400%) hue-rotate(180deg) brightness(95%) contrast(90%)",
                    }}
                  />
                  <span
                    className={`ml-2 text-lg ${open ? "block" : "hidden"} ${
                      current === menu.title ? "text-[#063269]" : "text-white"
                    }`}
                  >
                    {menu.title.charAt(0).toUpperCase() + menu.title.slice(1)}
                  </span>
                </div>
              </button>

              {/* Dropdown for Settings */}
              {menu.submenu && settingsOpen && (
                <div
                  className="absolute left-full top-0 bg-white shadow-2xl rounded-md w-48 z-[60]"
                  onMouseEnter={() => setSettingsOpen(true)}
                  onMouseLeave={() => setSettingsOpen(false)}
                >
                  {menu.submenu.map((sub, subIndex) => (
                    <button
                      key={subIndex}
                      onClick={() => setCurrent(sub.title)}
                      className={`block p-2 border-b-2 w-full text-left ${
                        current === sub.title
                          ? "text-[#3ebab8]"
                          : "text-gray-700"
                      } hover:bg-gray-100`}
                    >
                      {sub.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={`pt-4 transition-all duration-300 ${
          open ? "ml-72 w-full" : "ml-20 w-full"
        }`}
      >
        {renderPage()}

        {/* Footer */}
        <Footer />
      </main>
    </div>
  );
};

export default Sidebar;